import { Avatar, Tooltip, Popover, Stack, Text, Button, Loader } from '@mantine/core'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { api } from '@/api/client'
import { invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'

interface AssigneeAvatarProps {
  assignee: { id: number; full_name: string } | null
  taskRequestId: number
  tenantId: number | string | undefined
  canAssign: boolean
  /** Quando informado, lista candidatos via membros do time (gate chamados:approve)
   *  em vez de /v1/users (que exige usuarios:read). Usado em sub-tarefas. */
  teamTemplateId?: number | null
  /** Query keys extras para invalidar após atribuir (ex.: lista de sub-tarefas). */
  invalidateKeys?: unknown[][]
}

interface User {
  id: number
  full_name: string
  email: string
}

export function AssigneeAvatar({ assignee, taskRequestId, tenantId, canAssign, teamTemplateId, invalidateKeys }: AssigneeAvatarProps) {
  const queryClient = useQueryClient()

  const { data: users = [], isLoading } = useQuery<User[]>({
    queryKey: teamTemplateId
      ? ['team-members', tenantId, teamTemplateId]
      : ['tenant-users', tenantId],
    queryFn: async () => {
      if (teamTemplateId) {
        const res = await api.get<User[]>(
          `/v1/tenants/${tenantId}/chamados/teams/${teamTemplateId}/members`
        )
        return res.data ?? []
      }
      const res = await api.get<{ users: User[] }>(`/v1/users?tenant_id=${tenantId}`)
      return res.data.users ?? []
    },
    enabled: canAssign,
  })

  const mutation = useMutation({
    mutationFn: async (userId: number | null) => {
      await api.patch(`/v1/tenants/${tenantId}/workflow/${taskRequestId}/assign`, {
        assignee_user_id: userId,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chamado', String(taskRequestId)] })
      invalidarPendenciasDoChamado(queryClient)  // reatribuir muda o "atribuídos a mim"
      for (const key of invalidateKeys ?? []) {
        queryClient.invalidateQueries({ queryKey: key })
      }
      notifications.show({ color: 'green', message: 'Atendente atualizado.' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const initials = assignee
    ? assignee.full_name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
    : '?'

  if (!canAssign) {
    return (
      <Tooltip label={assignee?.full_name ?? 'Sem atendente'} withArrow>
        <Avatar size="sm" color={assignee ? 'blue' : 'gray'} radius="xl">
          {initials}
        </Avatar>
      </Tooltip>
    )
  }

  return (
    <Popover width={220} position="bottom-start" shadow="md">
      <Popover.Target>
        <Tooltip label={assignee ? `Atendente: ${assignee.full_name}` : 'Sem atendente — clique para atribuir'} withArrow>
          <Avatar
            size="sm"
            color={assignee ? 'blue' : 'gray'}
            radius="xl"
            style={{ cursor: 'pointer' }}
          >
            {initials}
          </Avatar>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="xs" fw={500} c="dimmed">Atribuir atendente</Text>
          {isLoading && <Loader size="xs" />}
          {users.map((u) => (
            <Button
              key={u.id}
              size="xs"
              variant={assignee?.id === u.id ? 'filled' : 'subtle'}
              fullWidth
              onClick={() => mutation.mutate(u.id)}
              loading={mutation.isPending}
            >
              {u.full_name}
            </Button>
          ))}
          {assignee && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              fullWidth
              onClick={() => mutation.mutate(null)}
            >
              Remover atendente
            </Button>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
